// Página de Perfumería (ruta "/perfumeria").
// Muestra posts traídos de Ghost CMS con la etiqueta 'perfumeria'.
// Incluye un hero animado con fondo dorado/ámbar, un grid de reseñas de fragancias,
// un botón de suscripción a novedades y un disclaimer legal al pie.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaInstagram, FaTiktok, FaArrowLeft } from 'react-icons/fa';
import Footer from '../../../components/footer/Footer';
import InfoCard from '../../../components/shared/InfoCard';
import SkeletonCard from '../../../components/shared/SkeletonCard';
import ProductRow from '../../../components/store/ProductRow';
import { getGhostPosts } from '../../../services/ghost';
import styles from './perfumeria.module.css';


const PerfumeriaLanding = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Trae hasta 9 posts con etiqueta 'perfumeria' al montar el componente
  useEffect(() => {
    const fetchPerfumeriaPosts = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const data = await getGhostPosts({ tagSlug: 'perfumeria', limit: 9 });
        setPosts(data.posts || []);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPerfumeriaPosts();
  }, []);

  return (
    <div id="perfumeria-landing-page" className={styles['page-wrapper']}>
      <Helmet>
        <title>Perfumería — Matias Cagliero</title>
        <meta name="description" content="Reseñas y análisis de fragancias de nicho y diseñador. Explorando el universo de la perfumería artística." />
        <link rel="canonical" href="https://cagliero.ar/perfumeria" />
        <meta property="og:title" content="Perfumería — Matias Cagliero" />
        <meta property="og:description" content="Reseñas y análisis de fragancias de nicho y diseñador. Explorando el universo de la perfumería artística." />
        <meta property="og:url" content="https://cagliero.ar/perfumeria" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Barra superior con breadcrumb y redes sociales */}
      <div className={styles['top-bar']}>
        <nav className={styles.breadcrumb}>
          <Link to="/" className={styles['back-button-breadcrumb']}>
            <FaArrowLeft className={styles['back-icon']} />
            <span>Inicio</span>
          </Link>
          <span className={styles['breadcrumb-separator']}>/</span>
          <span className={styles['breadcrumb-current']}>Perfumería</span>
        </nav>
        <div className={styles['top-bar-socials']}>
          <a
            href="https://www.instagram.com/matias_cagliero"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram de Matias Cagliero"
            className={styles['social-icon']}
          >
            <FaInstagram />
          </a>
          <a
            href="https://www.tiktok.com/@matias_cagliero"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok de Matias Cagliero"
            className={styles['social-icon']}
          >
            <FaTiktok />
          </a>
        </div>
      </div>

      <header className={styles['hero-section-v3']}>
        <div className={styles['hero-content']}>
          <h1 className={styles['hero-title']}>Explorando el Mundo de la Perfumería</h1>
          <p className={styles['hero-subtitle']}>
            Reseñas, notas y descubrimientos sobre fragancias de nicho y de diseñador.
          </p>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/form?tipo=perfumeria" className={styles['load-more-btn']}>
              Suscribite a las Novedades
            </Link>
          </div>
        </div>
      </header>

      <main className={styles['data-analysis-content-area']}>
        {/* Fila de productos del ecommerce (si está habilitada en el admin) */}
        <ProductRow category="perfumeria" />

        <section className={styles['section-v3']}>
          <h2 className={styles['section-title-v3']}>Reseñas y Artículos</h2>

          {/* Error de API */}
          {error && (
            <p className={styles['loading-message']}>
              No se pudo cargar el contenido. Verificá tu conexión e intentá recargar la página.
            </p>
          )}

          <div className={styles['posts-grid-container']}>
            {isLoading ? (
              // Muestra 6 skeletons mientras cargan los posts
              Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            ) : posts.length > 0 ? (
              posts.map(post => <InfoCard key={post.id} item={post} variant="list" />)
            ) : !error ? (
              <p className={styles['loading-message']}>No hay notas disponibles por el momento.</p>
            ) : null}
          </div>
        </section>
      </main>

      {/* Disclaimer: opiniones personales, sin vínculo comercial con las marcas */}
      <section className={styles['disclaimer-footer']}>
        <div className={styles['disclaimer-text-container']}>
          <p>
            <strong>Aviso legal y transparencia (Disclaimer):</strong> Este espacio es de carácter
            independiente y tiene como único fin compartir opiniones personales sobre perfumería.{' '}
            <strong>Matias Cagliero no posee vínculo comercial ni es representante oficial de las marcas
            mencionadas</strong>, salvo que se indique explícitamente en un artículo.
          </p>
        </div>
      </section>

      <Footer variant="lifestyle" />
    </div>
  );
};

export default PerfumeriaLanding;
